// Read / write the dynamic component editor canvas pan+zoom state to the URL
// query string. See src/SharedWeb/scripts/dynamic-component-editor/docs/
// DYNAMIC_COMPONENT_EDITOR_CANVAS.md section 1.2.
//
// Keys: zoom (number, 0.01-8), panX (integer px), panY (integer px).
// Writes are debounced and use history.replaceState so drags/zooms don't
// pollute navigation history.

export const ZOOM_MIN = 0.01;
export const ZOOM_MAX = 8;
const WRITE_DEBOUNCE_MS = 150;

let writeTimer = null;
let selectionWriteTimer = null;

// Debounced writes fire after navigation can have happened; only touch the URL of the page they were made on.
function replaceIfStillOn(pathname, buildParams) {
  if (window.location.pathname !== pathname) {
    return;
  }
  const params = buildParams(new URLSearchParams(window.location.search));
  const query = params.toString();
  window.history.replaceState(window.history.state, "", `${pathname}${query ? `?${query}` : ""}${window.location.hash}`);
}

export function readCanvasUrlState() {
  if (typeof window === "undefined") {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const hasAny = params.has("zoom") || params.has("panX") || params.has("panY");
  if (!hasAny) {
    return null;
  }
  const zoomRaw = parseFloat(params.get("zoom"));
  const panXRaw = parseFloat(params.get("panX"));
  const panYRaw = parseFloat(params.get("panY"));
  const zoom = Number.isFinite(zoomRaw) && zoomRaw > 0
    ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomRaw))
    : 1;
  const panX = Number.isFinite(panXRaw) ? Math.round(panXRaw) : 0;
  const panY = Number.isFinite(panYRaw) ? Math.round(panYRaw) : 0;
  return { zoom, panX, panY };
}

export function writeCanvasUrlState({ zoom, panX, panY }) {
  if (typeof window === "undefined") {
    return;
  }
  if (writeTimer) {
    clearTimeout(writeTimer);
  }
  const pathname = window.location.pathname;
  writeTimer = setTimeout(() => {
    writeTimer = null;
    replaceIfStillOn(pathname, params => {
      params.set("zoom", Number(zoom).toFixed(3).replace(/\.?0+$/, ""));
      params.set("panX", String(Math.round(panX || 0)));
      params.set("panY", String(Math.round(panY || 0)));
      return params;
    });
  }, WRITE_DEBOUNCE_MS);
}

export function clearCanvasUrlState() {
  if (typeof window === "undefined") {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  params.delete("zoom");
  params.delete("panX");
  params.delete("panY");
  const query = params.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", url);
}

// The selected element(s) as a comma-joined list of bare entity PKs under `sel`
// — list-shaped from day one so multi-select (#40) needs no URL migration.

export function readSelectionUrlState() {
  if (typeof window === "undefined") {
    return [];
  }
  const raw = new URLSearchParams(window.location.search).get("sel");
  if (!raw) {
    return [];
  }
  return raw.split(",").map(id => id.trim()).filter(Boolean);
}

export function writeSelectionUrlState(bareIds) {
  if (typeof window === "undefined") {
    return;
  }
  if (selectionWriteTimer) {
    clearTimeout(selectionWriteTimer);
  }
  const pathname = window.location.pathname;
  selectionWriteTimer = setTimeout(() => {
    selectionWriteTimer = null;
    replaceIfStillOn(pathname, params => {
      if (bareIds && bareIds.length) {
        params.set("sel", bareIds.join(","));
      } else {
        params.delete("sel");
      }
      return params;
    });
  }, WRITE_DEBOUNCE_MS);
}

export function clearSelectionUrlState() {
  writeSelectionUrlState([]);
}

// The focused breakpoint under `bp` — the breakpoint whose values the inspector
// edits and whose frame the selection boxes sit on. Default is implicit (no
// param), so a clean URL means the Default breakpoint. Same replaceState +
// debounce contract as `sel`, so a pasted URL restores the exact element on the
// exact breakpoint.

let breakpointWriteTimer = null;

export function readBreakpointUrlState() {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = new URLSearchParams(window.location.search).get("bp");
  return raw && raw.trim() ? raw.trim() : null;
}

export function writeBreakpointUrlState(alias) {
  if (typeof window === "undefined") {
    return;
  }
  if (breakpointWriteTimer) {
    clearTimeout(breakpointWriteTimer);
  }
  const pathname = window.location.pathname;
  breakpointWriteTimer = setTimeout(() => {
    breakpointWriteTimer = null;
    replaceIfStillOn(pathname, params => {
      // Default is the implicit base; only non-default breakpoints get a param.
      if (alias && alias !== "default") {
        params.set("bp", alias);
      } else {
        params.delete("bp");
      }
      return params;
    });
  }, WRITE_DEBOUNCE_MS);
}

// Task 206: visibility preview toggles under `vis` (encodeVisibilityPreview form). Client-only, like sel/bp.
let visibilityPreviewWriteTimer = null;

function readVisibilityPreviewUrlState() {
  if (typeof window === "undefined") {
    return "";
  }
  return new URLSearchParams(window.location.search).get("vis") ?? "";
}

function writeVisibilityPreviewUrlState(encoded) {
  if (typeof window === "undefined") {
    return;
  }
  if (visibilityPreviewWriteTimer) {
    clearTimeout(visibilityPreviewWriteTimer);
  }
  const pathname = window.location.pathname;
  visibilityPreviewWriteTimer = setTimeout(() => {
    visibilityPreviewWriteTimer = null;
    replaceIfStillOn(pathname, params => {
      if (encoded) {
        params.set("vis", encoded);
      } else {
        params.delete("vis");
      }
      return params;
    });
  }, WRITE_DEBOUNCE_MS);
}

export function canvasUrlVisibilityPreviewProvider() {
  return { read: readVisibilityPreviewUrlState, write: writeVisibilityPreviewUrlState };
}
